// src/components/LoginForm.js (or your file path)

import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { loginUser } from '../slices/userSlice';
import logo from '../assets/Best_National_Carports-logo-blk.svg';
import { AlertCircle, Eye, EyeOff } from 'react-feather';
import toast from 'react-hot-toast';
import PublicFooter from './PublicFooter';

const LoginForm = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [showPassword, setShowPassword] = useState(false);

	// Get auth state from Redux. These values come from your userSlice.
	const { isAuthenticated, status, error, self } = useSelector(
		(state) => state.auth
	);
	console.log('LoginForm isAuthenticated:', isAuthenticated);

	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm();

	// This is the submission handler that react-hook-form will call.
	// The 'data' object contains the form values { email, password }.
	const onSubmit = (data) => {
		dispatch(loginUser(data));
	};

	// This effect handles redirecting the user AFTER a successful login.
	// It watches the 'isAuthenticated' flag from your Redux state.
	useEffect(() => {
		if (isAuthenticated) {
			const lastVisitedPage = localStorage.getItem('lastVisitedPage');
			localStorage.removeItem('lastVisitedPage');
			const defaultLanding = self?.role === 'EMPLOYEE' ? '/clock-in' : '/dashboard';
			navigate(lastVisitedPage || defaultLanding);
		}
	}, [isAuthenticated, navigate, self?.role]);

	useEffect(() => {
		if (status === 'failed' && error) {
			let errorMessage = 'Login failed';
			if (typeof error === 'string') {
				errorMessage = error;
			} else if (error?.Message) {
				errorMessage = error.Message;
			} else if (error?.message) {
				errorMessage = error.message;
			}
			toast.error(errorMessage);
		}
	}, [status, error]);

	return (
		<div className="flex min-h-screen flex-col login-page">
			<div className="w-full p-5 flex flex-1 justify-center items-center flex-col">
			<div className="logo-login inline-flex">
				<img src={logo} alt="BNC logo" />
			</div>
			{/* Use handleSubmit from react-hook-form to trigger the onSubmit function */}
			<form onSubmit={handleSubmit(onSubmit)} className="w-80">
				<div className="bg-gray-100 login-form">
					<h1 className="text-2xl font-bold mb-2">Login</h1>
					<input
						{...register('email', { required: 'Your email is required' })}
						placeholder="Email"
						className="inline-block w-full p-2 my-2 border border-gray-300 rounded-md"
					/>
					{errors.email && (
						<span className="text-red-500 text-xs italic form-error">
							<AlertCircle size={20} />
							{errors.email.message}
						</span>
					)}
					<div className="relative">
						<input
							{...register('password', {
								required: 'Your password is required',
							})}
							type={showPassword ? 'text' : 'password'}
							placeholder="Password"
							className="inline-block w-full p-2 my-2 border border-gray-300 rounded-md password-input"
						/>
						<button
							type="button"
							className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
							aria-label={showPassword ? 'Hide password' : 'Show password'}
							aria-pressed={showPassword}
							onClick={() => setShowPassword((v) => !v)}
						>
							{showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
						</button>
					</div>
					{errors.password && (
						<span className="text-red-500 text-xs italic inline-block w-full mb-4 form-error">
							<AlertCircle size={20} /> {errors.password.message}
						</span>
					)}
				</div>

				{/* Display the API error message from Redux state */}
				{status === 'failed' && error?.message && (
					<span className="text-red-500 text-base italic inline-block w-full my-4 form-error">
						<AlertCircle size={20} />{' '}
						{/* Adjust error.message based on your API response structure */}
						{error.message.Message || error.message}
					</span>
				)}

				<div className="align-btn">
					{/* Disable button while the login request is loading */}
					<button
						type="submit"
						className="btn-login"
						disabled={status === 'loading'}
					>
						{status === 'loading' ? 'Logging in...' : 'Login'}
					</button>
				</div>
			</form>
			</div>
			<PublicFooter />
		</div>
	);
};

export default LoginForm;
